import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Button, Typography, Alert, Modal, Space } from 'antd';
import { MailOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import toast from 'react-hot-toast';

const { Title, Text, Link } = Typography;

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [forgotPasswordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  // Set page title
  useEffect(() => {
    document.title = 'Vitreous Support - Login';
    return () => {
      document.title = 'Vitreous Support - Support Ticket Platform';
    };
  }, []);

  // Vitreous Support logo with cache busting - memoized to prevent infinite re-renders
  const logoUrl = useMemo(() => {
    const basePath = process.env.PUBLIC_URL || '';
    const cacheBuster = `${Date.now()}_${Math.random()}`;
    return `${basePath}/logo.png?v=${cacheBuster}`.replace('//', '/');
  }, []); // Empty dependency array - only calculate once on mount

  const handleSubmit = async (values: any) => {
    setError('');
    setLoading(true);

    try {
      await login(values.email, values.password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Login failed';
      const errorCode = err.response?.data?.code;
      
      let displayError = '';
      
      // Show specific error messages based on error type
      if (errorCode === 'INVALID_CREDENTIALS' || errorMessage.includes('Invalid email or password')) {
        displayError = 'Invalid email or password. Please check your credentials and try again.';
      } else if (errorCode === 'ACCOUNT_DEACTIVATED' || errorMessage.includes('deactivated')) {
        displayError = 'Your account has been deactivated. Please contact an administrator.';
      } else if (errorMessage.includes('email')) {
        displayError = 'Invalid email format. Please enter a valid email address.';
      } else {
        displayError = errorMessage;
      }
      
      setError(displayError);
      toast.error(displayError);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (values: any) => {
    setForgotPasswordError('');
    setForgotPasswordLoading(true);

    try {
      const response = await api.post('/api/auth/request-password-reset', {
        email: values.email,
      });
      
      // Check if email was actually sent
      const emailSent = response.data?.emailSent;
      const message = response.data?.message || 'Password reset link sent to your email';
      
      if (emailSent === false) {
        // Email was not sent (likely disabled or not configured)
        toast('Password reset token generated, but email could not be sent. Please check server logs for the reset link or contact an administrator.', {
          icon: '⚠️',
          duration: 5000,
        });
        setForgotPasswordError('Email notifications are disabled or SMTP is not configured. Please contact an administrator for assistance.');
      } else {
        toast.success(message);
        setForgotPasswordOpen(false);
        forgotPasswordForm.resetFields();
      }
    } catch (error: any) {
      console.error('Password reset request error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send password reset email';
      setForgotPasswordError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const openForgotPassword = () => {
    const currentEmail = form.getFieldValue('email');
    if (currentEmail) {
      forgotPasswordForm.setFieldsValue({ email: currentEmail });
    }
    setForgotPasswordError('');
    setForgotPasswordOpen(true);
  };

  // Add CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        background: 'linear-gradient(135deg, #20B2AA 0%, #1E88E5 50%, #1E3A8A 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)
          `,
          opacity: 0.6,
        }}
      />

      {/* Centered Login Card */}
      <div
        style={{
          width: '100%',
          maxWidth: 480,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderRadius: 24,
          padding: '56px 48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          position: 'relative',
          zIndex: 1,
          animation: 'fadeInUp 0.6s ease-out',
        }}
      >
        {/* Logo/Branding at top */}
        <div style={{ marginBottom: 48, textAlign: 'center' }}>
          {logoUrl ? (
            <img 
              key={logoUrl}
              src={logoUrl} 
              alt="Vitreous Support Logo" 
              style={{ 
                maxWidth: '320px', 
                height: 'auto', 
                objectFit: 'contain',
                margin: '0 auto 32px',
                transition: 'opacity 0.3s ease',
              }}
              onError={(e) => {
                console.error('[Login] Failed to load logo from:', logoUrl);
              }}
              onLoad={() => {
                console.log('[Login] Logo loaded successfully from:', logoUrl);
              }}
            />
          ) : (
            <Title level={1} style={{ fontSize: 36, fontWeight: 700, margin: '0 0 32px', color: '#1a1a1a', letterSpacing: '-0.5px' }}>
              Vitreous Support
            </Title>
          )}
          <Title
            level={2}
            style={{
              fontSize: 28,
              fontWeight: 600,
              marginBottom: 0,
              color: '#1a1a1a',
              lineHeight: 1.3,
            }}
          >
            Welcome Back!
          </Title>
        </div>

          {/* Error Alert */}
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
              style={{ 
                marginBottom: 28, 
                borderRadius: 12,
                border: '1px solid #ffccc7',
                backgroundColor: '#fff2f0',
              }}
            />
          )}

          {/* Login Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            size="large"
            style={{ width: '100%' }}
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
              style={{ marginBottom: 24 }}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />}
                placeholder="Your Email"
                style={{
                  height: 52,
                  borderRadius: 12,
                  fontSize: 15,
                  border: '1.5px solid #e8e8e8',
                  transition: 'all 0.2s ease',
                  paddingLeft: 44,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#000';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.05)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e8e8e8';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please enter your password' }]}
              style={{ marginBottom: 32 }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />}
                placeholder="Your Password"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                style={{
                  height: 52,
                  borderRadius: 12,
                  fontSize: 15,
                  border: '1.5px solid #e8e8e8',
                  transition: 'all 0.2s ease',
                  paddingLeft: 44,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#000';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.05)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e8e8e8';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 24 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                style={{
                  height: 52,
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #20B2AA 0%, #1E88E5 50%, #1E3A8A 100%)',
                  border: 'none',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(30, 58, 138, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 58, 138, 0.3)';
                }}
              >
                {loading ? 'Signing In...' : 'Log in'}
              </Button>
            </Form.Item>

            {/* Forgot Password Link */}
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Link 
                onClick={openForgotPassword} 
                style={{ 
                  color: '#595959',
                  fontSize: 14,
                  fontWeight: 500,
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#595959';
                }}
              >
                Forgot password?
              </Link>
              <Link 
                onClick={() => navigate('/request-account')}
                style={{ 
                  color: '#595959',
                  fontSize: 14,
                  fontWeight: 500,
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#595959';
                }}
              >
                Need an account? Request one here
              </Link>
            </div>
          </Form>
        </div>

      {/* Forgot Password Modal */}
      <Modal
        title={
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
            Reset Password
          </Title>
        }
        open={forgotPasswordOpen}
        onCancel={() => {
          setForgotPasswordOpen(false);
          forgotPasswordForm.resetFields();
        }}
        footer={null}
        width={500}
        styles={{
          content: {
            borderRadius: 16,
            padding: '24px',
          },
        }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 24, fontSize: 14 }}>
          Enter your email address and we'll send you a link to reset your password.
        </Text>

        {forgotPasswordError && (
          <Alert
            message={forgotPasswordError}
            type="error"
            showIcon
            closable
            onClose={() => setForgotPasswordError('')}
            style={{ marginBottom: 20, borderRadius: 12 }}
          />
        )}

        <Form
          form={forgotPasswordForm}
          layout="vertical"
          onFinish={handleForgotPassword}
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: '#8c8c8c' }} />}
              placeholder="Email Address"
              style={{ 
                height: 44,
                borderRadius: 10,
                border: '1.5px solid #e8e8e8',
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setForgotPasswordOpen(false);
                  forgotPasswordForm.resetFields();
                }}
                disabled={forgotPasswordLoading}
                style={{
                  borderRadius: 10,
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={forgotPasswordLoading}
                style={{
                  backgroundColor: '#000',
                  borderColor: '#000',
                  borderRadius: 10,
                  fontWeight: 500,
                }}
              >
                {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Login;
